import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Zap,
} from "lucide-react";
import { useEditorStore } from "../store";
import { findNodeById } from "../tree-utils";

import { SectionCustomizer } from "./SectionCustomizer";

/** Theme colours offered for card backgrounds and text. */
const CARD_COLORS = [
  { value: "background", label: "Page background" },
  { value: "surface", label: "Card surface" },
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
  { value: "accent", label: "Accent" },
  { value: "text", label: "Text colour" },
];

export function InspectorStyle() {
  const document = useEditorStore((s) => s.document);
  const selectedNodeId = useEditorStore((s) => s.selectedNodeId);
  const viewport = useEditorStore((s) => s.viewport);
  const updateNodeProps = useEditorStore((s) => s.updateNodeProps);

  if (!selectedNodeId) {
    return (
      <div className="zirro-inspector-empty">
        <p>No component selected.</p>
      </div>
    );
  }

  const search = findNodeById(document, selectedNodeId);
  if (!search) return null;

  const { node } = search;
  if (node.type === "Section") {
    return <SectionCustomizer node={node} />;
  }
  const props = node.props ?? {};
  const responsiveObj = (node as { responsive?: Record<string, Record<string, unknown>>; overrides?: Record<string, Record<string, unknown>> });
  const overrides = responsiveObj.responsive?.[viewport] ?? responsiveObj.overrides?.[viewport] ?? {};
  const activeProps = viewport === "desktop" ? props : { ...props, ...overrides };

  const handleStyleChange = (key: string, value: unknown) => {
    updateNodeProps(node.id, { [key]: value }, viewport);
  };

  const isOverrideSet = (key: string) => {
    return viewport !== "desktop" && key in overrides;
  };

  const alignOptions = [
    { value: "left", label: "Left", icon: AlignLeft },
    { value: "center", label: "Center", icon: AlignCenter },
    { value: "right", label: "Right", icon: AlignRight },
    { value: "justify", label: "Justify", icon: AlignJustify },
  ];

  return (
    <div className="zirro-inspector-section">
      <div className="zirro-inspector-header">
        <h4>Style & Responsiveness</h4>
        <span className="zirro-badge zirro-badge-viewport">{viewport}</span>
      </div>

      {viewport !== "desktop" ? (
        <p className="zirro-hint zirro-override-notice">
          <Zap size={11} style={{ display: "inline", marginRight: 4 }} />
          Editing overrides for <strong>{viewport}</strong> breakpoint.
        </p>
      ) : null}

      <div className="zirro-field">
        <span className="zirro-field-label">
          Alignment {isOverrideSet("align") ? <Zap size={10} style={{ display: "inline" }} /> : ""}
        </span>
        <div className="zirro-segmented-control">
          {alignOptions.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                className={`zirro-segment ${activeProps.align === opt.value ? "active" : ""}`}
                onClick={() => handleStyleChange("align", opt.value)}
                title={opt.label}
              >
                <Icon size={14} />
              </button>
            );
          })}
        </div>
      </div>

      {node.type === "Heading" || node.type === "Text" ? (
        <>
          <label className="zirro-field">
            <span>
              Font Size {isOverrideSet(node.type === "Heading" ? "fontSize" : "size") ? <Zap size={10} style={{ display: "inline" }} /> : ""}
            </span>
            <select
              value={String(
                activeProps[node.type === "Heading" ? "fontSize" : "size"] ?? "base",
              )}
              onChange={(e) =>
                handleStyleChange(
                  node.type === "Heading" ? "fontSize" : "size",
                  e.target.value,
                )
              }
            >
              <option value="xs">XS (12px)</option>
              <option value="sm">SM (14px)</option>
              <option value="base">Base (16px)</option>
              <option value="lg">LG (18px)</option>
              <option value="xl">XL (20px)</option>
              <option value="2xl">2XL (24px)</option>
              <option value="3xl">3XL (30px)</option>
              <option value="4xl">4XL (36px)</option>
              <option value="5xl">5XL (48px)</option>
            </select>
          </label>

          <label className="zirro-field">
            <span>
              Block Max Width {isOverrideSet("maxWidth") ? <Zap size={10} style={{ display: "inline" }} /> : ""}
            </span>
            <select
              value={String(activeProps.maxWidth ?? "100%")}
              onChange={(e) =>
                handleStyleChange("maxWidth", e.target.value === "100%" ? undefined : e.target.value)
              }
            >
              <option value="100%">Full Width (100%)</option>
              <option value="480px">Compact (480px)</option>
              <option value="640px">Medium (640px)</option>
              <option value="768px">Standard (768px)</option>
            </select>
          </label>
        </>
      ) : null}

      {node.type === "Section" || node.type === "Container" ? (
        <>
          <label className="zirro-field">
            <span>
              Padding {isOverrideSet("padding") ? <Zap size={10} style={{ display: "inline" }} /> : ""}
            </span>
            <select
              value={String(activeProps.padding ?? "MD")}
              onChange={(e) => handleStyleChange("padding", e.target.value)}
            >
              <option value="none">None (0px)</option>
              <option value="XS">XS (4px)</option>
              <option value="SM">SM (8px)</option>
              <option value="MD">MD (16px)</option>
              <option value="LG">LG (32px)</option>
              <option value="XL">XL (64px)</option>
            </select>
          </label>

          <label className="zirro-field">
            <span>
              Gap / Spacing {isOverrideSet("gap") ? <Zap size={10} style={{ display: "inline" }} /> : ""}
            </span>
            <select
              value={String(activeProps.gap ?? "MD")}
              onChange={(e) => handleStyleChange("gap", e.target.value)}
            >
              <option value="none">None (0px)</option>
              <option value="XS">XS (4px)</option>
              <option value="SM">SM (8px)</option>
              <option value="MD">MD (16px)</option>
              <option value="LG">LG (32px)</option>
              <option value="XL">XL (64px)</option>
            </select>
          </label>
        </>
      ) : null}

      {node.type === "Container" ? (
        <div className="zirro-field-group">
          <h5>Card style</h5>
          <div className="zirro-field-row-2">
            <label className="zirro-field">
              <span>Background</span>
              <select
                value={CARD_COLORS.some((c) => c.value === activeProps.background) ? String(activeProps.background) : activeProps.background ? "custom" : ""}
                onChange={(e) => handleStyleChange("background", e.target.value === "custom" ? "#ffffff" : e.target.value || undefined)}
              >
                <option value="">None</option>
                {CARD_COLORS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
                <option value="custom">Custom colour</option>
              </select>
            </label>
            <label className="zirro-field">
              <span>Text colour</span>
              <select
                value={CARD_COLORS.some((c) => c.value === activeProps.textColor) ? String(activeProps.textColor) : ""}
                onChange={(e) => handleStyleChange("textColor", e.target.value || undefined)}
              >
                <option value="">Inherit</option>
                <option value="#ffffff">White</option>
                {CARD_COLORS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {activeProps.background && !CARD_COLORS.some((c) => c.value === activeProps.background) ? (
            <label className="zirro-field">
              <span>Custom background</span>
              <input
                type="color"
                value={/^#[0-9a-f]{6}$/i.test(String(activeProps.background)) ? String(activeProps.background) : "#ffffff"}
                onChange={(e) => handleStyleChange("background", e.target.value)}
              />
            </label>
          ) : null}
          <div className="zirro-field-row-2">
            <label className="zirro-field">
              <span>Corners</span>
              <select
                value={String(activeProps.borderRadius ?? "")}
                onChange={(e) => handleStyleChange("borderRadius", e.target.value || undefined)}
              >
                <option value="">Square</option>
                <option value="theme">Theme default</option>
                <option value="sm">Subtle</option>
                <option value="md">Rounded</option>
                <option value="lg">Soft</option>
                <option value="xl">Extra soft</option>
              </select>
            </label>
            <label className="zirro-field">
              <span>Shadow</span>
              <select
                value={String(activeProps.boxShadow ?? "")}
                onChange={(e) => handleStyleChange("boxShadow", e.target.value || undefined)}
              >
                <option value="">None</option>
                <option value="sm">Subtle</option>
                <option value="md">Medium</option>
                <option value="lg">Lifted</option>
              </select>
            </label>
          </div>
          <label className="zirro-field">
            <span>Border</span>
            <select
              value={String(activeProps.borderWidth ?? "")}
              onChange={(e) => handleStyleChange("borderWidth", e.target.value || undefined)}
            >
              <option value="">None</option>
              <option value="1px">Thin</option>
              <option value="2px">Medium</option>
            </select>
          </label>
        </div>
      ) : null}

      <label className="zirro-field">
        <span>Color Theme Token</span>
        <select
          value={String(activeProps.color ?? "text")}
          onChange={(e) => handleStyleChange("color", e.target.value)}
        >
          <option value="text">Body Text</option>
          <option value="primary">Primary Accent</option>
          <option value="secondary">Secondary Accent</option>
          <option value="accent">Highlight Accent</option>
          <option value="muted">Muted Subtext</option>
          <option value="background">Page Background</option>
          <option value="surface">Card Surface</option>
        </select>
      </label>
    </div>
  );
}
